// Toothbrush levels. Coordinates: 1200 x 800. The ball is a bath pearl (or a heavy glass marble), the goal is the happy tooth (bell) or the toothbrush mug (bowl).
// The id is fixed (progress is saved under it) – never change it once the level is released.
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import type { Ball, LevelData, ObjSpec, StaticData } from '../../core/types';
import type { WaterProps } from './shared';

// bathtub / washbasin: inside x0..x1, bottom y1, water surface wy
function tub(x0: number, x1: number, top: number, y1: number, wy: number, extra?: Partial<WaterProps>): { statics: StaticData[]; water: ObjSpec } {
  const wt = 26;
  return {
    statics: [
      { x: (x0 + x1) / 2, y: y1 + 13, w: x1 - x0 + 2 * wt, h: 26, skin: 'toothbrush_tub' },
      { x: x0 - wt / 2, y: (top + y1) / 2 + 6, w: wt, h: y1 - top + 14, skin: 'toothbrush_tub' },
      { x: x1 + wt / 2, y: (top + y1) / 2 + 6, w: wt, h: y1 - top + 14, skin: 'toothbrush_tub' },
    ],
    water: Object.assign({ t: 'toothbrush_water' as const, x0, x1, y: wy, y1 }, extra || {}),
  };
}
const P = 15; // bath pearl radius
const HEAVY: Pick<Ball, 'skin' | 'opts'> = { skin: 'toothbrush_heavy_ball', opts: { density: 0.006 } };
const L: LevelData[] = [];   // wk and wi are set by Registry.world

{ // 1 – Splash – new: water; the toothbrush (plank) guides the pearl into the water, the current carries it to the tooth
  const s1: StaticData = { x: 200, y: 250, w: 320, h: 22, a: 5, skin: 'toothbrush_shelf' };
  const T = tub(560, 1080, 400, 700, 500, { flow: 1.6 });
  L.push({
    id: 'splash', par: 1, tools: { plank: 2 }, learn: ['float'], intro: 'toothbrush_water',
    statics: [s1, ...T.statics, { x: 290, y: 690, w: 520, h: 26, skin: 'toothbrush_tile' }],
    balls: [on(s1, -130, P)],
    goal: { type: 'bell', x: 1020, y: 485 },
    objs: [T.water, { t: 'duck', x: 740, y: 420 }],
    solution: [{ t: 'plank', x: 420, y: 320, a: 15 }],
  });
}

{ // 2 – Bubbles – new tool: the bath whale; it blows the pearl up to the tooth
  const T = tub(480, 720, 440, 690, 520);
  L.push({
    id: 'bubbles', par: 1, tools: { toothbrush_bubbles: 1 }, learn: ['air'], intro: 'toothbrush_bubbles',
    statics: [...T.statics, { x: 300, y: 300, w: 220, h: 22, skin: 'toothbrush_shelf' }, { x: 900, y: 300, w: 220, h: 22, skin: 'toothbrush_shelf' }],
    balls: [{ x: 600, y: 505, r: P }],
    goal: { type: 'bell', x: 600, y: 300 },
    objs: [T.water, { t: 'duck', x: 690, y: 380 }],
    solution: [{ t: 'toothbrush_bubbles', x: 600, y: 660, a: 0 }],
  });
}
{ // 3 – Sponge bridge – new tool: the sponge; it floats and becomes a bridge over the water
  L.push({
    id: 'sponge-bridge', par: 1, tools: { toothbrush_sponge: 1 }, learn: ['float'], intro: 'toothbrush_sponge',
    statics: [{ x: 300, y: 520, w: 400, h: 240, skin: 'toothbrush_tile' }, { x: 710, y: 520, w: 180, h: 240, skin: 'toothbrush_tile' },
      { x: 560, y: 630, w: 140, h: 20, skin: 'toothbrush_tub' }, { x: 970, y: 760, w: 360, h: 30, skin: 'toothbrush_tile' }],
    balls: [{ x: 140, y: 384, r: P, vx: 4 }],
    goal: { type: 'bowl', x: 895, y: 745, w: 170 },
    objs: [{ t: 'toothbrush_water', x0: 500, x1: 620, y: 434, y1: 620, snap: true }, { t: 'duck', x: 830, y: 470 }],
    solution: [{ t: 'toothbrush_sponge', x: 560, y: 340 }],
  });
}

{ // 4 – Sponge or soap – new tool: the soap; only what floats becomes a bridge (the soap sinks)
  const s1: StaticData = { x: 190, y: 200, w: 300, h: 22, a: 14, skin: 'toothbrush_shelf' };
  L.push({
    id: 'sponge-or-soap', par: 1, tools: { toothbrush_soap: 1, toothbrush_sponge: 1 }, learn: ['float', 'sink'], intro: 'toothbrush_soap',
    statics: [s1, { x: 450, y: 470, w: 380, h: 220, skin: 'toothbrush_tile' }, { x: 840, y: 470, w: 160, h: 220, skin: 'toothbrush_tile' },
      { x: 700, y: 570, w: 140, h: 20, skin: 'toothbrush_tub' }, { x: 1030, y: 760, w: 300, h: 30, skin: 'toothbrush_tile' }, { x: 1160, y: 560, w: 34, h: 400, skin: 'toothbrush_tile' }],
    balls: [on(s1, -120, P)],
    goal: { type: 'bowl', x: 1060, y: 745, w: 150 },
    objs: [{ t: 'toothbrush_water', x0: 640, x1: 760, y: 394, y1: 560, snap: true }, { t: 'duck', x: 1010, y: 470 }],
    solution: [{ t: 'toothbrush_sponge', x: 700, y: 300 }],
    fails: [[{ t: 'toothbrush_soap', x: 700, y: 300 }]],
  });
}
{ // 5 – The boat – the heavy glass marble sinks, but the boat can carry it
  const s1: StaticData = { x: 180, y: 220, w: 300, h: 22, a: 8, skin: 'toothbrush_shelf' };
  const T = tub(420, 1080, 420, 700, 520, { flow: 1.4 });
  L.push({
    id: 'boat', par: 1, tools: { plank: 2 }, learn: ['float', 'sink'],
    statics: [s1, ...T.statics],
    balls: [on(s1, -120, P, HEAVY)],
    goal: { type: 'bell', x: 1030, y: 476 },
    objs: [T.water, { t: 'toothbrush_boat', x: 530, y: 516 }, { t: 'duck', x: 760, y: 470 }],
    solution: [{ t: 'plank', x: 400, y: 360, a: 30 }],
    fails: [[{ t: 'plank', x: 700, y: 330, a: 30 }]],
  });
}
{ // 6 – Two bubbles – the heavy marble lies on the bottom; one whale is too little air
  const T = tub(460, 740, 420, 690, 470);
  L.push({
    id: 'two-bubbles', par: 2, tools: { toothbrush_bubbles: 2 }, learn: ['air', 'sink'],
    statics: [...T.statics, { x: 240, y: 330, w: 260, h: 22, skin: 'toothbrush_shelf' }],
    balls: [{ x: 600, y: 445, r: P, skin: 'toothbrush_heavy_ball', opts: { density: 0.006 } }],
    goal: { type: 'bell', x: 600, y: 370 },
    objs: [T.water, { t: 'duck', x: 240, y: 280 }],
    solution: [{ t: 'toothbrush_bubbles', x: 560, y: 668, a: 0 }, { t: 'toothbrush_bubbles', x: 640, y: 668, a: 0 }],
    fails: [[{ t: 'toothbrush_bubbles', x: 600, y: 668, a: 0 }], [{ t: 'toothbrush_bubbles', x: 600, y: 560, a: 0 }]],
  });
}

{ // 7 – The tap – the soap sinks onto the button, the water rises and lifts the pearl up to the tooth (the sponge floats = decoy)
  const A = tub(170, 290, 430, 640, 470, { snap: true });
  const B = tub(600, 800, 230, 690, 640, { rise: { to: 300, v: 1.2, on: 'hane', tx: 650, ty: 170 } });
  L.push({
    id: 'tap', par: 1, tools: { toothbrush_sponge: 1, toothbrush_soap: 1 }, learn: ['sink', 'float'],
    statics: [...A.statics, ...B.statics],
    balls: [{ x: 700, y: 630, r: P }],
    goal: { type: 'bell', x: 730, y: 262 },
    objs: [A.water, B.water, { t: 'button', x: 230, y: 626, act: { fire: 'hane' } }, { t: 'duck', x: 660, y: 330 }],
    solution: [{ t: 'toothbrush_soap', x: 230, y: 360 }],
    fails: [[{ t: 'toothbrush_sponge', x: 230, y: 360 }]],
  });
}
{ // 8 – Two holes – two sponge bridges; the soap sinks
  const s1: StaticData = { x: 150, y: 190, w: 240, h: 22, a: 12, skin: 'toothbrush_shelf' };
  L.push({
    id: 'two-holes', par: 2, tools: { toothbrush_soap: 2, toothbrush_sponge: 2 }, learn: ['float', 'sink'],
    statics: [s1, { x: 302, y: 470, w: 285, h: 220, skin: 'toothbrush_tile' }, { x: 620, y: 470, w: 130, h: 220, skin: 'toothbrush_tile' }, { x: 897, y: 470, w: 205, h: 220, skin: 'toothbrush_tile' },
      { x: 500, y: 570, w: 140, h: 20, skin: 'toothbrush_tub' }, { x: 740, y: 570, w: 140, h: 20, skin: 'toothbrush_tub' }, { x: 1080, y: 760, w: 220, h: 30, skin: 'toothbrush_tile' }, { x: 1170, y: 560, w: 34, h: 400, skin: 'toothbrush_tile' }],
    balls: [on(s1, -90, P)],
    goal: { type: 'bowl', x: 1075, y: 745, w: 150 },
    objs: [{ t: 'toothbrush_water', x0: 445, x1: 555, y: 394, y1: 560, snap: true }, { t: 'toothbrush_water', x0: 685, x1: 795, y: 394, y1: 560, snap: true }, { t: 'duck', x: 1080, y: 480 }],
    solution: [{ t: 'toothbrush_sponge', x: 500, y: 300 }, { t: 'toothbrush_sponge', x: 740, y: 300 }],
    fails: [[{ t: 'toothbrush_sponge', x: 500, y: 300 }, { t: 'toothbrush_soap', x: 740, y: 300 }], [{ t: 'toothbrush_soap', x: 500, y: 300 }, { t: 'toothbrush_sponge', x: 740, y: 300 }]],
  });
}
{ // 9 – Out of the bath – the current takes the pearl to the whale, which blows it over the edge (the plank is a decoy)
  const T = tub(260, 760, 420, 690, 500, { flow: 1.5 });
  L.push({
    id: 'out-of-the-tub', par: 1, tools: { toothbrush_bubbles: 1, plank: 1 }, learn: ['air', 'float'],
    statics: [...T.statics, { x: 960, y: 760, w: 380, h: 30, skin: 'toothbrush_tile' }],
    balls: [{ x: 320, y: 480, r: P }],
    goal: { type: 'bowl', x: 900, y: 745, w: 170 },
    objs: [T.water, { t: 'duck', x: 1000, y: 330 }],
    solution: [{ t: 'toothbrush_bubbles', x: 640, y: 668, a: 15 }],
    fails: [[{ t: 'toothbrush_bubbles', x: 320, y: 668, a: 0 }], [{ t: 'plank', x: 860, y: 450, a: 15 }]],
  });
}
{ // 10 – Who carries it? – the boat carries; one whale cannot lift the heavy marble (the whale is a decoy)
  const s1: StaticData = { x: 1030, y: 230, w: 280, h: 22, a: -8, skin: 'toothbrush_shelf' };
  const T = tub(120, 780, 420, 700, 520, { flow: -1.3 });
  L.push({
    id: 'who-carries', par: 1, tools: { toothbrush_bubbles: 1, plank: 1 }, learn: ['float', 'sink', 'air'],
    statics: [s1, ...T.statics],
    balls: [on(s1, 110, P, HEAVY)],
    goal: { type: 'bell', x: 170, y: 476 },
    objs: [T.water, { t: 'toothbrush_boat', x: 660, y: 516 }, { t: 'duck', x: 420, y: 380 }],
    solution: [{ t: 'plank', x: 800, y: 370, a: -30 }],
    fails: [[{ t: 'toothbrush_bubbles', x: 860, y: 668, a: 0 }], [{ t: 'toothbrush_bubbles', x: 720, y: 668, a: 0 }]],
  });
}

{ // 11 – Which way? – the current runs to the left; the whale must stand where the pearl ends up
  const s1: StaticData = { x: 1060, y: 250, w: 240, h: 22, a: -10, skin: 'toothbrush_shelf' };
  const T = tub(440, 980, 420, 690, 500, { flow: -1.5 });
  L.push({
    id: 'which-way', par: 1, tools: { toothbrush_bubbles: 1, toothbrush_sponge: 1 }, learn: ['air', 'float'],
    statics: [s1, ...T.statics, { x: 230, y: 760, w: 380, h: 30, skin: 'toothbrush_tile' }],
    balls: [on(s1, 90, P)],
    goal: { type: 'bowl', x: 300, y: 745, w: 170 },
    objs: [T.water, { t: 'duck', x: 330, y: 330 }],
    solution: [{ t: 'toothbrush_bubbles', x: 540, y: 668, a: -15 }],
    fails: [[{ t: 'toothbrush_bubbles', x: 900, y: 668, a: -15 }], [{ t: 'toothbrush_sponge', x: 900, y: 460 }]],
  });
}
{ // 12 – Bridge and bubbles – the sponge carries the pearl over the hole, the whale lifts it up to the tooth (two steps; the soap is a decoy)
  const s1: StaticData = { x: 140, y: 230, w: 240, h: 22, a: 12, skin: 'toothbrush_shelf' };
  const T = tub(620, 860, 500, 690, 540);
  L.push({
    id: 'bridge-and-bubbles', par: 2, tools: { toothbrush_soap: 1, toothbrush_sponge: 1, toothbrush_bubbles: 1 }, learn: ['float', 'air'],
    statics: [s1, { x: 235, y: 530, w: 330, h: 220, skin: 'toothbrush_tile' }, { x: 550, y: 530, w: 80, h: 220, skin: 'toothbrush_tile' },
      { x: 455, y: 630, w: 130, h: 20, skin: 'toothbrush_tub' }, ...T.statics],
    balls: [on(s1, -90, P)],
    goal: { type: 'bell', x: 740, y: 300 },
    objs: [{ t: 'toothbrush_water', x0: 400, x1: 510, y: 454, y1: 620, snap: true }, T.water, { t: 'duck', x: 960, y: 330 }],
    solution: [{ t: 'toothbrush_sponge', x: 455, y: 340 }, { t: 'toothbrush_bubbles', x: 690, y: 668, a: 0 }],
    fails: [[{ t: 'toothbrush_soap', x: 455, y: 340 }, { t: 'toothbrush_bubbles', x: 690, y: 668, a: 0 }], [{ t: 'toothbrush_sponge', x: 455, y: 340 }]],
  });
}
{ // 13 – Lift the boat – chain reaction: the soap slides under the shelf, sinks onto the button, and the water lifts the boat with the marble
  const T = tub(140, 620, 290, 700, 600, { flow: 1.2, rise: { to: 330, v: 1.1, on: 'hane', tx: 330, ty: 120 } });
  const A = tub(900, 1020, 520, 690, 560, { noplace: 330 });
  const roof: StaticData = { x: 990, y: 300, w: 300, h: 22, skin: 'toothbrush_shelf' };
  L.push({
    id: 'lift-the-boat', par: 2, tools: { plank: 1, toothbrush_soap: 1, toothbrush_sponge: 1 }, learn: ['sink', 'float', 'chain_reaction'],
    statics: [...T.statics, ...A.statics, roof],
    balls: [{ x: 250, y: 574, r: P, skin: 'toothbrush_heavy_ball', opts: { density: 0.006 } }],
    goal: { type: 'bell', x: 500, y: 288 },
    objs: [T.water, A.water, { t: 'toothbrush_boat', x: 250, y: 596 }, { t: 'button', x: 960, y: 676, act: { fire: 'hane' } }, { t: 'duck', x: 760, y: 560 }],
    solution: [{ t: 'plank', x: 780, y: 400, a: 30 }, { t: 'toothbrush_soap', x: 740, y: 280 }],
    fails: [[{ t: 'plank', x: 780, y: 400, a: 30 }, { t: 'toothbrush_sponge', x: 740, y: 280 }], [{ t: 'toothbrush_soap', x: 740, y: 280 }]],
  });
}

{ // 14 – Hop in the bath – the toothpaste tube (spring) sends the pearl over the edge; the current and the whale do the rest
  const s1: StaticData = { x: 160, y: 220, w: 260, h: 22, a: 10, skin: 'toothbrush_shelf' };
  const T = tub(540, 1060, 380, 690, 470, { flow: 1.5 });
  L.push({
    id: 'hop-in-the-bath', par: 2, tools: { pad: 1, toothbrush_bubbles: 1, plank: 1, toothbrush_sponge: 1 }, learn: ['spring', 'air', 'float'],
    statics: [s1, ...T.statics, { x: 250, y: 712, w: 440, h: 26, skin: 'toothbrush_tile' }],
    balls: [on(s1, -100, P)],
    goal: { type: 'bell', x: 1000, y: 300 },
    objs: [T.water, { t: 'duck', x: 470, y: 300 }],
    solution: [{ t: 'pad', x: 400, y: 440, a: 15 }, { t: 'toothbrush_bubbles', x: 1000, y: 668, a: 0 }],
    fails: [[{ t: 'pad', x: 400, y: 440, a: 15 }], [{ t: 'toothbrush_bubbles', x: 1000, y: 668, a: 0 }]],
  });
}

{ // 15 – Master – bridge over the hole, down into the tub, the current and the whale up to the tooth (several ways)
  const s1: StaticData = { x: 130, y: 170, w: 220, h: 22, a: 12, skin: 'toothbrush_shelf' };
  const T = tub(860, 1100, 520, 700, 590, { flow: 1.4 });
  L.push({
    id: 'master', par: 3, tools: { toothbrush_sponge: 1, toothbrush_soap: 1, toothbrush_bubbles: 1, plank: 2, pad: 1 }, learn: ['float', 'sink', 'air', 'ramp'],
    statics: [s1, { x: 380, y: 370, w: 280, h: 80, skin: 'toothbrush_tile' }, { x: 690, y: 370, w: 120, h: 80, skin: 'toothbrush_tile' },
      { x: 575, y: 402, w: 130, h: 20, skin: 'toothbrush_tub' }, ...T.statics, { x: 420, y: 712, w: 840, h: 26, skin: 'toothbrush_tile' }],
    balls: [on(s1, -80, P)],
    goal: { type: 'bell', x: 1040, y: 320 },
    objs: [{ t: 'toothbrush_water', x0: 520, x1: 630, y: 364, y1: 392, snap: true }, T.water, { t: 'duck', x: 760, y: 560 }],
    solution: [{ t: 'toothbrush_sponge', x: 575, y: 240 }, { t: 'plank', x: 800, y: 420, a: 30 }, { t: 'toothbrush_bubbles', x: 1040, y: 668, a: 0 }],
    fails: [[{ t: 'toothbrush_soap', x: 575, y: 240 }, { t: 'plank', x: 800, y: 420, a: 30 }, { t: 'toothbrush_bubbles', x: 1040, y: 668, a: 0 }],
      [{ t: 'toothbrush_sponge', x: 575, y: 240 }, { t: 'plank', x: 800, y: 420, a: 30 }]],
  });
}

export const levels = defineLevels(L);
